<template>
    <div class="condition-header">
        <ConditionSelector
            v-if="hierarchyData"
            :component-selectors="componentSelectors"
            :is-pending="hierarchyIsPending"
            :is-error="isErrorHierarchy"
            :bookmark-id="bookmarkId"
            :component-type="ComponentType.Generator"
            @update-value="onValueUpdate"
            @reset-values="onResetValues"
        />
        <ConditionStatusHeaderActions
            v-if="generatorId && isContentDisplayed"
            :key="generatorId"
            :component-id="generatorId"
            :general-status="generatorData?.generalStatus"
            :last-synced-date="generatorData?.lastSyncedDate"
            :is-pending="generatorIsPending"
            :is-error="isErrorGenerator"
            :component-type="ComponentType.Generator"
            :is-no-action-set="generatorData?.isNoActionSet"
            :no-action-component="generatorData?.noActionComponent"
            @export-button-click="onExportButtonClick"
        />
    </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import { flattenGeneratorsHierarchy, useGeneratorsHierarchyQuery } from '@/api/queries/generators/useGeneratorsHierarchyQuery'
import { ComponentSelectorConfiguration, plant, plantGroup, generator } from '@/configuration/component/component-selector/component-selector-configuration'
import { ComponentType } from '@/configuration/component/component-types'
import ConditionSelector from '@/components/component-selector/ComponentSelector.vue'
import { computed, ref, watch } from 'vue'
import { SelectOptions } from '@/components/form-inputs/common/select-option'
import { useComponentStatusQuery } from '@/api/queries/general/useComponentStatusQuery'
import ConditionStatusHeaderActions from '@/components/condition-status-header-actions/ConditionStatusHeaderActions.vue'
import { toRef } from 'vue'
import { useGeneratorsExportStateStore } from '@/store/generators/exportStateStore'

const props = defineProps<{
    generatorId?: string
    isContentDisplayed: boolean
}>()

const generatorIdRef = toRef(() => props.generatorId)
const generatorsExportStateStore = useGeneratorsExportStateStore()

const { data: hierarchyData, isPending: hierarchyIsPending, isError: isErrorHierarchy } = useGeneratorsHierarchyQuery()
const { data: generatorData, isPending: generatorIsPending, isError: isErrorGenerator } = useComponentStatusQuery(generatorIdRef, ComponentType.Generator)

const generatorList = computed(() => {
    if (!hierarchyData.value?.plantGroups) {
        return []
    }

    return flattenGeneratorsHierarchy(hierarchyData.value.plantGroups)
})

const bookmarkId = computed(
    () => generatorList.value.find((e) => e.generator.id === props.generatorId)?.generator.bookmarkId,
)

const getComponentSelectors = () => {
    const componentStructure = ComponentSelectorConfiguration[ComponentType.Generator]
    const selectedGenerator = props.generatorId ? generatorList.value.find((e) => e.generator.id === props.generatorId) : undefined

    if (props.generatorId && !selectedGenerator) {
        router.replace({
            name: 'Generators_ConditionStatus',
            params: {
                generatorId: undefined,
            },
        })
    }

    let plantGroupSid: number | undefined = undefined
    let plantSid: number | undefined = undefined

    if (selectedGenerator) {
        plantGroupSid = selectedGenerator.plantGroup.sid
        plantSid = selectedGenerator.plant.sid
    }

    return componentStructure.map((selector) => {
        let options: SelectOptions<string | number, { isBookmarked?: boolean }> = []
        let modelValue = undefined
        let disabled = false

        if (selector.name === plantGroup) {
            modelValue = plantGroupSid
            options = getOptionsForDropdown(selector.name)
        } else if (selector.name === plant) {
            modelValue = plantSid
            options = getOptionsForDropdown(selector.name, plantGroupSid?.toString())
            disabled = !plantGroupSid
        } else if (selector.name === generator) {
            modelValue = selectedGenerator?.generator.id
            options = getOptionsForDropdown(selector.name, plantSid?.toString())
            disabled = !plantSid
        }

        return {
            ...selector,
            options,
            isLastChild: selector.name === generator,
            modelValue,
            disabled,
        }
    })
}

const getOptionsForDropdown = (dropdownName: string, parentValue?: string) => {
    if (dropdownName === plantGroup) {
        return hierarchyData.value?.plantGroups?.map((e) => ({
            label: e.name ?? '-',
            value: e.sid ?? 0,
        })) ?? []
    } else if (dropdownName === plant) {
        if (!parentValue) {
            return []
        }
        const selectedPlantGroup = hierarchyData.value?.plantGroups?.find((e) => e.sid?.toString() === parentValue)
        if (!selectedPlantGroup?.plants) {
            return []
        }
        return selectedPlantGroup.plants.map((e) => ({
            label: e.name ?? '-',
            value: e.sid ?? 0,
        }))
    } else if (dropdownName === generator) {
        if (!parentValue) {
            return []
        }
        const selectedPlant = hierarchyData.value?.plantGroups?.flatMap((e) => e.plants ?? [])?.find((e) => e.sid?.toString() === parentValue)
        if (!selectedPlant?.generators) {
            return []
        }
        const plantList = selectedPlant.generators.map((e) => ({
            label:
                !e.functionalLocation || e.generatorSid === e.functionalLocation
                    ? `${e.generatorSid} / - / ${e.generatorName}`
                    : `${e.generatorSid} / ${e.functionalLocation} / ${e.generatorName}`,
            value: e.generatorSid ?? '',
            extraData: {
                isBookmarked: !!e.bookmarkId,
            },
        }))
        return plantList.toSorted((a) => {
            return a.extraData.isBookmarked ? -1 : 1
        })
    } else {
        return []
    }
}

let componentSelectors = ref(hierarchyData.value ? getComponentSelectors() : [] )

watch(
    [() => hierarchyData.value],
    ([data]) => {
        if (!data) {
            return
        }
        componentSelectors.value = getComponentSelectors()
    },
    { immediate: true },
)

const router = useRouter()

const onValueUpdate = (payload: { [key: string]: string | number | undefined }) => {
    let updatedValueIndex: null | number = null
    componentSelectors.value = componentSelectors.value.map((selector, index) => {
        if (selector.name in payload) {
            updatedValueIndex = index
            return {
                ...selector,
                modelValue: payload[selector.name],
            }
        } else if (updatedValueIndex !== null && index === updatedValueIndex + 1) {
            const updatedValue = Object.values(payload)[0]?.toString()
            return {
                ...selector,
                disabled: !updatedValue,
                options: getOptionsForDropdown(selector.name, updatedValue)
            }
        }
        return selector
    })
    if (payload[generator]) {
        router.replace({
            name: 'Generators_ConditionStatus',
            params: {
                generatorId: payload[generator],
            },
        })
    }
}

const onResetValues = () => {
    componentSelectors.value = componentSelectors.value.map((selector) => {
        return {
            ...selector,
            modelValue: undefined,
            disabled: selector.name !== plantGroup,
            options: selector.name === plantGroup ? getOptionsForDropdown(selector.name) : []
        }
    })
    router.replace({
        name: 'Generators_ConditionStatus',
        params: {
            generatorId: undefined,
        },
    })
}

const onExportButtonClick = () => {
    router.push({
        name: 'Export_Generators_ConditionStatus',
        params: {
            generatorId: props.generatorId,
        },
        query: generatorsExportStateStore.conditionStatusExportParameters,
    })
}
</script>

<style scoped lang="scss">
.condition-header {
    @include spacing-page;

    display: flex;
    flex-direction: column;
    gap: $spacing-m;

    background-color: $pbl-foreground;
    border-bottom: 1px solid $pbl-border-muted;
}
</style>
